// Streams PCM to ElevenLabs' Scribe v2 Realtime WebSocket straight from the
// browser and yields partial/committed utterances. A browser WebSocket can't
// send the xi-api-key header, so a 15-minute single-use token is minted over
// fetch first (the documented client-side flow) — same key-in-bundle tradeoff
// as before, the key just rides the mint request instead of the socket.
const TOKEN_URL =
  'https://api.elevenlabs.io/v1/single-use-token/realtime_scribe'
const SOCKET_URL = 'wss://api.elevenlabs.io/v1/speech-to-text/realtime'
const MODEL_ID = 'scribe_v2_realtime'
// Pin transcription to English (ISO 639-1). Scribe otherwise auto-detects across
// 90+ languages and can flip the whole call's transcript to another language on
// a stray foreign word or noisy audio — this desk is English-only.
const LANGUAGE_CODE = 'en'

// Chunks that arrive while the token mints / socket connects are queued so the
// first words of the call aren't lost; the cap keeps a connection that never
// opens from accumulating audio forever (~85ms per chunk ⇒ ~4s of speech).
const MAX_PENDING_CHUNKS = 50

// String.fromCharCode spread block size for base64 conversion — stays well
// under engines' argument-count limits.
const BASE64_BLOCK = 8192

export interface TranscriptResult {
  text: string
  /** False for a partial (still-mutating) hypothesis of the same utterance. */
  isFinal: boolean
  /** Utterance start, ms from the beginning of the audio stream. */
  startMs: number
}

type SocketStatus = 'live' | 'error' | 'closed'

export interface TranscriptionSocket {
  sendAudio: (samples: Float32Array) => void
  close: () => void
}

interface OpenOptions {
  apiKey: string
  sampleRate: number
  onResult: (result: TranscriptResult) => void
  onStatus: (status: SocketStatus) => void
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

async function mintToken(apiKey: string): Promise<string> {
  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'xi-api-key': apiKey },
  })
  if (!response.ok) throw new Error(`token mint failed: ${response.status}`)
  const data: unknown = await response.json()
  if (!isRecord(data) || typeof data['token'] !== 'string') {
    throw new Error('malformed token response')
  }
  return data['token']
}

/** Float32 samples → the `input_audio_chunk` frame (16-bit PCM, base64). */
function toChunkMessage(samples: Float32Array): string {
  const pcm = new Int16Array(samples.length)
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]))
    pcm[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff
  }
  const bytes = new Uint8Array(pcm.buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i += BASE64_BLOCK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_BLOCK))
  }
  return JSON.stringify({
    message_type: 'input_audio_chunk',
    audio_base_64: btoa(binary),
  })
}

// With include_timestamps=true the server emits BOTH `committed_transcript`
// and `committed_transcript_with_timestamps` for the same utterance — honour
// only the timestamped one or every line lands twice (once as [00:00]).
const RESULT_TYPES = new Set([
  'partial_transcript',
  'committed_transcript_with_timestamps',
])

/** Start of the first word, when the committed frame carries timestamps. */
function firstWordStartMs(data: Record<string, unknown>): number {
  const words = data['words']
  if (!Array.isArray(words)) return 0
  const first: unknown = words[0]
  if (!isRecord(first) || typeof first['start'] !== 'number') return 0
  return first['start'] * 1000
}

/** Extract an utterance from a transcript frame; null for anything else
 *  (session_started, empty transcripts, unparseable payloads). */
function parseResult(raw: string): TranscriptResult | null {
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return null
  }
  if (!isRecord(data)) return null
  const type = data['message_type']
  if (typeof type !== 'string' || !RESULT_TYPES.has(type)) return null
  const text = typeof data['text'] === 'string' ? data['text'].trim() : ''
  if (text === '') return null
  return {
    text,
    isFinal: type !== 'partial_transcript',
    startMs: firstWordStartMs(data),
  }
}

/** ElevenLabs reports failures as in-band frames (auth_error, quota_exceeded,
 *  rate_limited, …) rather than only closing the socket. */
function isErrorFrame(raw: string): boolean {
  let data: unknown
  try {
    data = JSON.parse(raw)
  } catch {
    return false
  }
  if (!isRecord(data) || typeof data['message_type'] !== 'string') return false
  const type = data['message_type']
  return (
    type.endsWith('_error') ||
    type === 'quota_exceeded' ||
    type === 'rate_limited'
  )
}

// A network drop mid-call kills this socket, but telephony keeps the same call
// (and its audio) alive underneath, so the transcript must re-establish itself
// rather than stay dead until the call ends. Reconnect on any unexpected close
// with a capped backoff (a real auth/quota failure must not hammer the token
// endpoint), and immediately when the browser reports `online` — that's the
// common "went offline for a few seconds, came back" case.
const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 8_000

function socketUrl(token: string, sampleRate: number): string {
  const query = new URLSearchParams({
    token,
    model_id: MODEL_ID,
    audio_format: `pcm_${Math.round(sampleRate)}`,
    // Force English instead of auto-detect, so the transcript can't drift into
    // another language mid-call (see LANGUAGE_CODE).
    language_code: LANGUAGE_CODE,
    // Let the server's voice-activity detection decide when an utterance is
    // final — mirrors how partial/final results behaved on Deepgram.
    commit_strategy: 'vad',
    include_timestamps: 'true',
  })
  return `${SOCKET_URL}?${query.toString()}`
}

function dispatchMessage(
  event: MessageEvent<unknown>,
  options: OpenOptions,
): void {
  if (typeof event.data !== 'string') return
  if (isErrorFrame(event.data)) {
    options.onStatus('error')
    return
  }
  const result = parseResult(event.data)
  if (result) options.onResult(result)
}

// Drop a socket's handlers before closing it, so a deliberate teardown can't
// surface a spurious error/closed status or trigger a reconnect.
function silenceSocket(ws: WebSocket): void {
  ws.onopen = null
  ws.onmessage = null
  ws.onerror = null
  ws.onclose = null
  ws.close()
}

// A single Scribe stream for one call, self-healing across network drops: the
// call (and its audio) outlives a socket death, so the transcript reconnects
// itself rather than staying dead until the call ends.
class ReconnectingTranscriptionSocket implements TranscriptionSocket {
  private socket: WebSocket | undefined
  private closed = false
  // True while a token mint / socket open is in flight, so an `online` event
  // racing a backoff retry can't spin up a second socket on the same call.
  private connecting = false
  private attempts = 0
  private retryTimer: ReturnType<typeof setTimeout> | undefined
  private readonly pending: string[] = []

  constructor(private readonly options: OpenOptions) {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline)
    }
    this.connect()
  }

  sendAudio(samples: Float32Array): void {
    const frame = toChunkMessage(samples)
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(frame)
    else if (this.pending.length < MAX_PENDING_CHUNKS) this.pending.push(frame)
  }

  close(): void {
    this.closed = true
    this.clearRetry()
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline)
    }
    if (this.socket) silenceSocket(this.socket)
  }

  private clearRetry(): void {
    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer)
      this.retryTimer = undefined
    }
  }

  private connect(): void {
    if (this.closed || this.connecting) return
    if (this.socket?.readyState === WebSocket.OPEN) return
    this.connecting = true
    void mintToken(this.options.apiKey)
      .then((token) => {
        this.connecting = false
        if (!this.closed) this.openSocket(token)
      })
      .catch(() => {
        this.connecting = false
        if (!this.closed) this.scheduleReconnect()
      })
  }

  private openSocket(token: string): void {
    const ws = new WebSocket(socketUrl(token, this.options.sampleRate))
    this.socket = ws
    ws.onopen = () => {
      this.attempts = 0
      this.options.onStatus('live')
      for (const frame of this.pending) ws.send(frame)
      this.pending.length = 0
    }
    ws.onmessage = (event: MessageEvent<unknown>) =>
      dispatchMessage(event, this.options)
    ws.onerror = () => this.options.onStatus('error')
    // An unexpected close (network drop, server teardown) reconnects; a
    // deliberate close() nulls this handler first, so it can't fire then.
    ws.onclose = () => this.scheduleReconnect()
  }

  private scheduleReconnect(): void {
    if (this.closed || this.connecting) return
    if (this.attempts >= MAX_RECONNECT_ATTEMPTS) {
      // Give up the automatic loop and report the drop. An `online` event still
      // refills the budget, so a long outage can recover later.
      this.options.onStatus('closed')
      return
    }
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** this.attempts,
      RECONNECT_MAX_MS,
    )
    this.attempts += 1
    this.clearRetry()
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined
      this.connect()
    }, delay)
  }

  // Network came back — retry now instead of waiting out the backoff, and refill
  // the attempt budget so recovery after a long outage isn't blocked by it. An
  // arrow field so add/removeEventListener share one stable reference.
  private readonly handleOnline = (): void => {
    if (this.closed || this.connecting) return
    if (this.socket?.readyState === WebSocket.OPEN) return
    this.attempts = 0
    this.clearRetry()
    this.connect()
  }
}

export function openTranscriptionSocket(
  options: OpenOptions,
): TranscriptionSocket {
  return new ReconnectingTranscriptionSocket(options)
}
